/**
 * Voice Provider Types
 *
 * Type definitions for multi-provider voice configuration supporting
 * different STT (Speech-to-Text) and TTS (Text-to-Speech) providers.
 */

// Provider identifiers
export type STTProviderInternal = 'deepgram' | 'elevenlabs' | 'soniox';
export type STTProvider = STTProviderInternal;
export type TTSProvider = 'elevenlabs' | 'cartesia' | 'minimax';

// Base configuration interface
interface BaseSTTConfig {
  provider: STTProviderInternal;
  language?: string;
}

// Deepgram's only English-only model; pairing it with any other language fails server validation.
export const DEEPGRAM_EN_ONLY_MODEL = 'flux-general-en';

// Deepgram STT model types (matching schema validation)
export type DeepgramSTTModel =
  | typeof DEEPGRAM_EN_ONLY_MODEL // Low-latency streaming model
  | 'flux-general-multi' // Multilingual low-latency streaming model
  | 'nova-3' // Latest general purpose
  | 'nova-2'; // General purpose

// Deepgram STT Configuration
export interface STTDeepgramConfig extends BaseSTTConfig {
  provider: 'deepgram';
  model: DeepgramSTTModel;
  // Flux model settings
  eot_threshold?: number;
  eot_timeout_ms?: number;
  // Nova model settings
  endpointing?: number | false;
  utterance_end_ms?: number;
  // Shared settings
  keywords?: string[]; // For nova-2 (with intensifier, format: "word:intensifier")
  keyterm?: string[]; // For flux and nova-3
  // Formatting settings
  numerals?: boolean;
  smart_format?: boolean;
}

// Discriminated union for STT config
export type STTConfig = STTDeepgramConfig;

// Model settings definition for dynamic UI rendering
export interface STTModelSettings {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select' | 'slider' | 'text' | 'multi-select';
  default?: unknown;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  description?: string;
  translationKey?: string;
  multiline?: boolean;
}

// Model metadata for UI
export interface STTModelInfo {
  id: string;
  name: string;
  provider: STTProviderInternal;
  supportedLanguages: string[];
  settings: STTModelSettings[];
}

// Provider metadata for UI
export interface STTProviderInfo {
  id: STTProviderInternal;
  name: string;
  logo?: string;
}

// TTS configuration types (keeping existing ElevenLabs structure)
export interface TTSElevenLabsConfig {
  provider: 'elevenlabs';
  voice_id?: string;
  model_id?: string;
  language_code?: string;
  inactivity_timeout?: number;
  auto_mode?: boolean;
  apply_text_normalization?: 'auto' | 'on' | 'off';
  voice_settings?: { stability?: number; similarity_boost?: number; speed?: number };
}

// Cartesia emotion types for voice synthesis
export type CartesiaEmotion = 'neutral' | 'calm' | 'content';

// Cartesia TTS Configuration
export interface TTSCartesiaConfig {
  provider: 'cartesia';
  voice_id?: string;
  model_id?: string;
  language_code?: string;
  volume?: number; // 0-2, default 1.0
  speed?: number; // 0-2, default 1.0
  emotion?: CartesiaEmotion;
}

// MiniMax TTS model types
export type MiniMaxModel = 'speech-2.8-hd' | 'speech-2.8-turbo';

// MiniMax TTS Configuration
// Voices are multilingual; language is selected via language_boost (ISO code).
export interface TTSMiniMaxConfig {
  provider: 'minimax';
  voice_id?: string;
  model_id?: MiniMaxModel;
  language_boost?: string;
  speed?: number; // 0.5-2, default 1.0
  volume?: number; // 0-10, default 1.0
  pitch?: number; // -12 to 12, default 0
  english_normalization?: boolean;
}

// Union type for TTS config
export type TTSConfig = TTSElevenLabsConfig | TTSCartesiaConfig | TTSMiniMaxConfig;

// TTS Provider metadata for UI
export interface TTSProviderInfo {
  id: TTSProvider;
  name: string;
  logo?: string;
}

// TTS Model metadata for UI
export interface TTSModelInfo {
  id: string;
  name: string;
  provider: TTSProvider;
  languages?: string[];
}

// TTS Voice metadata for UI
export interface TTSVoiceInfo {
  id: string;
  name: string;
  description?: string;
  language?: string;
  provider: TTSProvider;
  accent?: string;
  previewUrl?: string;
}

// Type guard helpers
export function isDeepgramSTTConfig(config: STTConfig): config is STTDeepgramConfig {
  return config.provider === 'deepgram';
}

export function isElevenLabsTTSConfig(config: TTSConfig): config is TTSElevenLabsConfig {
  return config.provider === 'elevenlabs';
}

export function isCartesiaTTSConfig(config: TTSConfig): config is TTSCartesiaConfig {
  return config.provider === 'cartesia';
}

export function isMiniMaxTTSConfig(config: TTSConfig): config is TTSMiniMaxConfig {
  return config.provider === 'minimax';
}
